<template>
  <v-app class="secondary">
    <v-toolbar dark v-if="!$route.meta.hideNav" tabs prominent color="#032f9e" class="pl-4 pr-4"> <!-- #primary -->
      <v-btn icon v-if="$route.meta.parent !== null" :to="parentRoute" exact>
        <v-icon>arrow_back</v-icon>
      </v-btn>
      <div id="app-logo">
        <img style="max-height: 43px;" src="/img/newfocus-white-text-mini.png" />
      </div>
      <v-tabs color="#032f9e" fixed-tabs grow slot="extension">
        <v-tab
            v-for="item in navItems"
            :key="item.title"
            :to="item.path"
            exact>
          {{ item.title }}
        </v-tab>
      </v-tabs>
      <v-spacer></v-spacer>
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn
              v-on="on"
              flat icon color="red"
              v-show="showSubscriptionWarning"
              to="/subscriptions">
            <v-icon>notification_important</v-icon>
          </v-btn>
        </template>
        <span>Subscription is expiring soon. Click here for details.</span>
      </v-tooltip>
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn
              v-on="on"
              flat icon color="orange"
              v-show="isOffline"
              @click="tryReconnect">
            <v-icon>warning</v-icon>
          </v-btn>
        </template>
        <span>Application is offline. Click here to reconnect.</span>
      </v-tooltip>
    </v-toolbar>
    <v-content>
      <template v-if="$auth.ready()">
          <router-view></router-view>
      </template>
      <template v-if="!$auth.ready()">
          Loading ...
      </template>
    </v-content>
    <v-footer height="8.5em" color="#222" class="px-5">
      <v-layout column align-center fill-height class="pt-2">
        <v-flex align-content-center>
          <p class="text-xs-center" style="color: #666;">Designed by FuZion Videos LLC - Mother organization of New Focus</p>
        </v-flex>
      </v-layout>
    </v-footer>
    <v-snackbar
        v-model="showMsg"
        :color="msgType"
        top
        absolute
    >
      <span><v-icon>{{ msgType }}</v-icon> {{ msgText }}</span>
    </v-snackbar>
    <v-snackbar
      :value="showInstallationPromptIndicator"
      bottom
      :multi-line="true"
      :timeout="0"
      color="accent"
    >
      <v-layout row>
        <v-flex grow>
          <v-btn
            @click="pwaPromptInstallation"
            color="primary"
            block
            class="ma-0"
            style="width: 100%"
          >
            Install App for Easier Access
          </v-btn>
        </v-flex>
        <v-flex shrink>
          <v-btn
            @click="pwaInstallationPromptFinished = true"
            color="primary"
            block
            icon
            class="ml-2"
          >
            <v-icon>cancel</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>
    <v-dialog
      v-model="pwaShowPromptDialog"
    >
      <v-card>
        <v-card-title class="text-xs-center">
          <p class="headline" style="width: 100%">Press the share button, then "Add to Home Screen"</p>
          <p class="subheading" style="width: 100%">(you may have to scroll to see the share button)</p>
        </v-card-title>
        <v-card-text>
          <v-layout justify-center>
            <v-flex shrink>
              <v-icon>arrow_downward</v-icon>
            </v-flex>
          </v-layout>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
const { DateTime } = require('luxon')

export default {
  name: 'App',
  data () {
    return {
      isSubscriptionExpiring: false,
      isOffline: false,
      tryReconnectTimeoutId: null,
      tryReconnectInterval: null,

      pwaStandaloneMode: false,
      pwaShowPromptDialog: false,
      pwaInstallationPrompt: null,
      pwaInstallationPromptFinished: false,

      devotionalCompletions: [],

      showMsg: false,
      msgType: '',
      msgText: ''
    }
  },
  computed: {
    isGoogleAvailable: function () {
      return !!process.env.VUE_APP_PWA_OAUTH2_GOOGLE_CLIENT_ID
    },
    isFacebookAvailable: function () {
      return !!process.env.VUE_APP_PWA_OAUTH2_FACEBOOK_CLIENT_ID
    },
    showSubscriptionWarning: function () {
      if (this.isSubscriptionExpiring === false || this.$route.path === '/subscriptions') {
        return false
      } else {
        return true
      }
    },
    isMobileSafari: function () {
      let ua = window.navigator.userAgent

      let isMobile = /iP(ad|hone)/i.test(ua)
      let isWebkit = /WebKit/i.test(ua)
      let isChrome = /CriOS/i.test(ua)

      return isMobile && isWebkit && !isChrome
    },
    showInstallationPromptIndicator: function () {
      // Make sure we aren't on a functional page
      let routes = [
        '/confirm',
        '/setup'
      ]
      let path = this.$route.path
      if (this.$route.meta && this.$route.meta.simplePath) {
        path = this.$route.meta.simplePath
      }
      if (routes.indexOf(path) !== -1) {
        return false
      }

      return this.pwaInstallationPromptFinished === false &&
        ((this.isMobileSafari && !this.pwaStandaloneMode) || this.pwaInstallationPrompt !== null)
    },
    parentRoute: function () {
      // If we have a direct value use it, otherwise we need to calculate
      if (this.$route.meta.parent !== undefined) {
        return this.$route.meta.parent
      } else {
        // Our general rule is to take the path and just remove the last part
        let path = this.$route.path.split('/')
        path.pop()
        path = path.join('/')

        // If we ended up with nothing, correct it to a valid path
        if (path === '') {
          path = '/'
        }

        return path
      }
    },
    navItems: function () {
      // Return a usable list of navigation items that are meant to be rendered
      return this.$router.options.routes.map(function (route) {
        return {
          title: route.meta.displayName,
          path: (route.meta.simplePath) ? route.meta.simplePath : route.path,
          shouldRender: function () {
            // If we don't have a valid display name, we don't render
            if (route.meta.displayName === null) {
              return false
            }

            // If we are a sub-page, we don't render
            if (route.meta.isSubpage && route.meta.isSubpage === true) {
              return false
            }

            // Otherwise, we base the decision on authentication status
            switch (typeof route.meta.auth) {
              case 'undefined':
                return true
              case 'boolean':
                return this.$auth.check() === route.meta.auth
              // string, array, or object will be handled the same way by
              // the auth check
              default:
                return this.$auth.check(route.meta.auth)
            }
          }.bind(this)
        }
      }, this).filter(function (item) {
        return item.shouldRender()
      })
    },
    devotionalCompletionsInWeek: function () {
      // Get the bounds of our current week (Luxon uses ISO weeks, so the week
      // runs Monday through Sunday, so we need to subtract a day at the front
      // end; we keep the end date to allow for the entire Saturday's span)
      let now = DateTime.local()
      let start = now.startOf('week').startOf('day').minus({ day: 1 }).valueOf()
      let end = now.endOf('week').startOf('day').valueOf()

      // Our completions are sorted recent first, so we only need to check until
      // we've reached one completion outside our week
      let counter = 0
      for (let c of this.devotionalCompletions) {
        let t = DateTime.fromSQL(c.created_at, { zone: 'UTC' })
          .setZone('America/Chicago') // XXX: Time zone should be configurable
          .valueOf()
        if (t >= start && t < end) {
          counter++
        } else {
          break
        }
      }

      return counter
    },
    devotionalCompletionsInMonth: function () {
      // Get the bounds of our current month
      let now = DateTime.local()
      let start = now.startOf('month').startOf('day').valueOf()
      let end = now.endOf('month').endOf('day').valueOf()

      // Our completions are sorted recent first, so we only need to check until
      // we've reached one completion outside our month
      let counter = 0
      for (let c of this.devotionalCompletions) {
        let t = DateTime.fromSQL(c.created_at, { zone: 'UTC' })
          .setZone('America/Chicago') // XXX: Time zone should be configurable
          .valueOf()
        if (t >= start && t <= end) {
          counter++
        } else {
          break
        }
      }

      return counter
    },
    devotionalCompletionsInYear: function () {
      // Get the bounds of our current year
      let now = DateTime.local()
      let start = now.startOf('year').startOf('day').valueOf()
      let end = now.endOf('year').endOf('day').valueOf()

      // Our completions are sorted recent first, so we only need to check until
      // we've reached one completion outside our year
      let counter = 0
      for (let c of this.devotionalCompletions) {
        let t = DateTime.fromSQL(c.created_at, { zone: 'UTC' })
          .setZone('America/Chicago') // XXX: Time zone should be configurable
          .valueOf()
        if (t >= start && t <= end) {
          counter++
        } else {
          break
        }
      }

      return counter
    }
  },
  mounted: function () {
    // Set up an interval for checking our subscription status and run it once
    // right away (allowing a little bit of time for setup to finish)
    let checkSubscription = async () => {
      let s = await this.$auth.subscriptions()
      if (s.length === 0) {
        return
      }

      // We only care about the farthest-reaching subscription
      let sub = s[0]
      if (sub.startDate <= (new Date()) && sub.nextCycle === null) {
        this.isSubscriptionExpiring = true
      } else {
        this.isSubscriptionExpiring = false
      }
    }
    setTimeout(checkSubscription, 2000)
    setInterval(checkSubscription, (1000 * 60 * 60 * 12)) // every 12 hours
  },
  methods: {
    updateDevotionalCompletions: function () {
      // Send an API request for our devotional completions, and update our
      // global value accordingly
      this.axios.get('/devotionals/completions')
        .then((res) => {
          this.devotionalCompletions = res.data.sort((a, b) => {
            if (a.created_at < b.created_at) {
              return 1
            }
            if (a.created_at > b.created_at) {
              return -1
            }
            return 0
          })
        })
        .catch(() => { /* swallow exception */ })
    },
    tryReconnect: function (ev) {
      var _this = this
      this.axios.get('/status').then(function (res) {
        // Not offline anymore
        _this.isOffline = false
        clearTimeout(_this.tryReconnectTimeoutId)
        _this.tryReconnectTimeoutId = null
      }).catch(function (res) {
        // Still offline
        // Unless this test was user-initiated, increase our reconnection
        // interval by 2x, up to a max of 5 minutes
        if (!ev) {
          _this.tryReconnectInterval = Math.min(_this.tryReconnectInterval * 2, (1000 * 60 * 5))
          _this.tryReconnectTimeoutId = setTimeout(_this.tryReconnect, _this.tryReconnectInterval)
        }
      })
    },
    pwaPromptInstallation: function () {
      // Mobile Safari requires our own instructional dialog
      if (this.isMobileSafari) {
        this.pwaShowPromptDialog = true
        this.pwaInstallationPromptFinished = true
        return
      }

      if (this.pwaInstallationPrompt === null) {
        return
      }

      // Show the prompt and wait for a response
      this.pwaInstallationPrompt.prompt()
      this.pwaInstallationPromptFinished = true
      this.pwaInstallationPrompt.userChoice
        .then(() => {
          this.pwaInstallationPrompt = null
        })
    },
    // Helper utility to show a flash message from anywhere in the app
    showFlashMessage: function (msg, type = 'error') {
      this.msgText = msg
      this.msgType = type
      this.showMsg = true
    },
    // Generator method for handling API errors. It parses the error response
    // generically before calling a custom handler for rendering (if provided).
    // The custom handler will be passed the following arguments:
    // * errMessage - A broad-level description of the problem
    // * errors - Individual, field-level descriptions of specific problems
    // * err - The original error response
    apiErrorHandler: function (customHandler) {
      return (err) => {
        // If we're not in production, we can print out the details of the error
        // for sake of debugging
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.log('API Request Error:', JSON.stringify(err))
        }

        // Make sure our response is in the format we're expecting. Otherwise,
        // we might be getting an error because we're offline and requesting a
        // non-cached resource. If neither of those is the case, we'll report a
        // generic failure, but there's not much else we can do.
        var errMessage = ''
        var errors = {}
        if (err && err.response) {
          // If we have message values, use them directly. Otherwise, make up a
          // generic error message based on our status code.
          if (err.response.data && err.response.data.err) {
            if (typeof err.response.data.err === 'string') {
              errMessage = err.response.data.err
            } else {
              errors = err.response.data.err
              if (err.response.data.msg) {
                errMessage = err.response.data.msg
              }
            }
          } else {
            switch (err.response.status) {
              case 400:
                errMessage = 'There was a problem with the input you provided'
                break
              case 401:
                errMessage = 'You must be logged in to access the resource you requested'
                break
              case 402:
                errMessage = 'You must have an active subscription to access the resource you requested'
                break
              case 403:
                errMessage = 'You do not have permission to access the resource you requested'
                break
              case 404:
                errMessage = 'The resource you requested does not exist, or you did not specify a resource'
                break
              case 405:
                errMessage = 'The resource you requested does not accept the action you tried to take'
                break
              case 409:
                errMessage = 'Your request conflicts with another resource'
                break
              default:
                errMessage = 'There was a problem accessing the resource you requested'
                break
            }
          }
        } else if (this.isOffline) {
          errMessage = 'The resource you requested is not currently available offline'
        } else {
          errMessage = 'There was a problem making a network request. Please try again.'
        }

        // Now that we've parsed the response, we can call the custom handler if
        // it was given. The handler can return something other than true (or not
        // return a value) to prevent our default rendering scheme.
        var shouldRender = true
        if (customHandler && typeof customHandler === 'function') {
          shouldRender = customHandler(errMessage, errors, err)
        }

        // If there was no handler or if it returned true, we show the error as
        // a notification banner.
        if (shouldRender) {
          this.showFlashMessage(errMessage)
        }
      }
    },
    logout: function () {
      this.$auth.logout({ makeRequest: true })
    },
    googleLogin () {
      this.$auth.oauth2({
        provider: 'google',
        rememberMe: true,
        params: {
          client_id: process.env.VUE_APP_SYNC_OAUTH2_GOOGLE_CLIENT_ID,
          scope: 'openid email profile',
          redirect_uri: this.$auth.options.getUrl() + '/oauth2?type=google'
        }
      })
    },
    facebookLogin () {
      this.$auth.oauth2({
        provider: 'facebook',
        rememberMe: true,
        params: {
          client_id: process.env.VUE_APP_SYNC_OAUTH2_FACEBOOK_CLIENT_ID,
          display: 'popup',
          redirect_uri: this.$auth.options.getUrl() + '/oauth2?type=facebook'
        }
      })
    },
    parseApiDateTimeString: function (d) {
      let year = d.substring(0, 4)
      let month = (d.substring(5, 7)) - 1 // PHP has months as 1-12, JS as 0-11
      let day = d.substring(8, 10)
      let hour = d.substring(11, 13)
      let minute = d.substring(14, 16)
      let second = d.substring(17, 19)

      return new Date(Date.UTC(year, month, day, hour, minute, second))
    },
    getApiDateTimeString: function (d) {
      if (typeof date === 'undefined') {
        d = new Date()
      }

      return d.getUTCFullYear() + '-' +
        ('0' + (d.getUTCMonth() + 1)).slice(-2) + '-' +
        ('0' + d.getUTCDate()).slice(-2) + ' ' +
        ('0' + d.getUTCHours()).slice(-2) + ':' +
        ('0' + d.getUTCMinutes()).slice(-2) + ':' +
        ('0' + d.getUTCSeconds()).slice(-2)
    },
    getAuthenticatedFileURL: function (fileID, allowCache) {
      let tag = ''
      if (allowCache === true) {
        tag = '&cache'
      }
      return this.axios.defaults.baseURL + '/files/' + fileID + '?bearer_token=' + this.$auth.token() + tag
    }
  }
}
</script>
